import Decimal from 'decimal.js';
import { D } from '@/relatorios/numeros';
import { copiarCifsLoteSchema, type ApropriacaoEntrada, type ApropriacaoResponse, type CopiarCifsLoteDto, type GradeCopiaCifsResponse } from '@triade/contracts';
import { decimalDigitado, decimalParaTextoCampo } from '@/lib/format';

/**
 * Logica da grade de "Copiar do mes" (S27). Sem regra de negocio: valida o texto digitado, soma com decimal.js (so para mostrar o total
 * origem x destino) e monta o corpo do lote. Quem decide o que e pulado, o destino fechado e o rateio e a API.
 */
export interface LinhaGrade {
  cifId: string;
  identificador: string;
  descricao: string;
  itemCifNome: string | null;
  valorOrigem: string;
  /** Novo valor como o usuario ve/digita ("1.200,50"). */
  texto: string;
  marcada: boolean;
  /** Ja existe no destino: a API pula; a linha fica desabilitada. */
  existeNoDestino: boolean;
  apropriacao: ApropriacaoResponse | null;
  /** Apropriacao editada pelo usuario (null = manter a da origem). */
  editada: ApropriacaoEntrada | null;
}

export function linhasIniciais(g: GradeCopiaCifsResponse): LinhaGrade[] {
  return g.linhas.map((l) => ({
    cifId: l.cifId,
    identificador: l.identificador,
    descricao: l.descricao,
    itemCifNome: l.itemCifNome,
    valorOrigem: l.valor,
    texto: decimalParaTextoCampo(l.valor),
    marcada: !l.existeNoDestino,
    existeNoDestino: l.existeNoDestino,
    apropriacao: l.apropriacao,
    editada: null,
  }));
}

/**
 * Valor digitado como string decimal da API; vazio/invalido = null. Recusa o que a API recusaria (mais de 12 inteiros ou 6 casas) e o
 * formato en-US colado ("1,234.56"), que seria lido errado como pt-BR.
 */
export function valorDigitado(l: LinhaGrade): string | null {
  if (/^\s*(R\$\s*)?\d{1,3}(,\d{3})+\.\d+\s*$/.test(l.texto)) return null;
  const v = decimalDigitado(l.texto);
  return v !== null && /^\d{1,12}(\.\d{1,6})?$/.test(v) ? v : null;
}

/**
 * Cola uma coluna do Excel (linhas separadas por quebra de linha; em copia de varias colunas vale a primeira) a partir da linha `inicio`.
 * Pula as linhas que ja existem no destino. Celula vazia nao altera; sobra de linhas e ignorada.
 */
export function colarColuna(linhas: LinhaGrade[], inicio: number, colado: string): LinhaGrade[] {
  const celulas = colado.replace(/\r/g, '').replace(/\n$/, '').split('\n').map((l) => (l.split('\t')[0] ?? '').trim());
  const saida = linhas.map((l) => ({ ...l }));
  let k = 0;
  for (let i = inicio; i < saida.length && k < celulas.length; i += 1) {
    const alvo = saida[i]!;
    if (alvo.existeNoDestino) continue;
    const c = celulas[k]!;
    k += 1;
    if (c !== '') alvo.texto = c.replace(/^R\$\s*/, '');
  }
  return saida;
}

export interface TotaisGrade {
  /** Soma dos valores da origem (todas as linhas). */
  origem: string;
  /** Soma dos novos valores das linhas marcadas (o que sera gravado). */
  aCopiar: string;
  /** O que o destino ja tem + o que sera gravado. */
  destino: string;
  /** Linhas marcadas com valor invalido. */
  invalidas: number;
  marcadas: number;
}

export function totaisGrade(linhas: LinhaGrade[], jaNoDestino: string): TotaisGrade {
  let origem = new D(0);
  let aCopiar = new D(0);
  let invalidas = 0;
  let marcadas = 0;
  for (const l of linhas) {
    origem = origem.plus(l.valorOrigem);
    if (!l.marcada || l.existeNoDestino) continue;
    marcadas += 1;
    const v = valorDigitado(l);
    if (v === null) invalidas += 1;
    else aCopiar = aCopiar.plus(v);
  }
  return { origem: origem.toString(), aCopiar: aCopiar.toString(), destino: aCopiar.plus(jaNoDestino).toString(), invalidas, marcadas };
}

/** Corpo do POST /cifs/copiar/lote; null se nada marcado ou houver valor invalido. */
export function corpoLote(linhas: LinhaGrade[], origem: string, destino: string): CopiarCifsLoteDto | null {
  const marcadas = linhas.filter((l) => l.marcada && !l.existeNoDestino);
  if (!marcadas.length) return null;
  const itens = [];
  for (const l of marcadas) {
    const valor = valorDigitado(l);
    if (valor === null) return null;
    itens.push({ cifId: l.cifId, valor, ...(l.editada ? { apropriacao: l.editada } : {}) });
  }
  const r = copiarCifsLoteSchema.safeParse({ origem, destino, linhas: itens });
  return r.success ? r.data : null;
}

/** Apropriacao da API como entrada editavel (ponto de partida do editor). */
export function entradaDeResposta(a: ApropriacaoResponse | null): ApropriacaoEntrada | null {
  if (!a) return null;
  if (a.tipo === 'CRITERIO') return a.criterioId ? { tipo: 'CRITERIO', criterioId: a.criterioId } : null;
  if (a.tipo === 'IGUAL') return { tipo: 'IGUAL', centroIds: a.linhas.map((l) => l.centroId) };
  return { tipo: 'MANUAL', linhas: a.linhas.map((l) => ({ centroId: l.centroId, percentual: l.percentual })) };
}

/** "Prensagem 30% · Acabamento 20%" / "Critério M2" / "—" (so apresentacao do que a API devolveu). */
export function resumoApropriacao(a: ApropriacaoResponse | null, editada: ApropriacaoEntrada | null): string {
  if (editada) return editada.tipo === 'MANUAL' ? `${editada.linhas.length} centro(s) — editada` : editada.tipo === 'IGUAL' ? `${editada.centroIds.length} centro(s) iguais — editada` : 'Critério — editada';
  if (!a) return '—';
  if (a.tipo === 'CRITERIO') return `Critério ${a.criterioNome ?? ''}`.trim();
  return a.linhas.map((l) => `${l.nome} ${new Decimal(l.percentual).times(100).toDecimalPlaces(1).toString()}%`).join(' · ') || '—';
}
